import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateTax, formatCurrency } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const TaxCalculator = () => {
  const navigate = useNavigate();
  const [annualIncome, setAnnualIncome] = useState(1200000);
  const [deductions, setDeductions] = useState(150000);

  const taxResult = useMemo(
    () => calculateTax(annualIncome, deductions),
    [annualIncome, deductions]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                Income Tax Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-blue-100 p-3">
                      <Receipt className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        Income Tax Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        FY 2024-25 Tax Estimation
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Annual income"
                      value={annualIncome}
                      onChange={setAnnualIncome}
                      min={100000}
                      max={10000000}
                      step={10000}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Total Deductions (80C...)"
                      value={deductions}
                      onChange={setDeductions}
                      min={0}
                      max={1000000}
                      step={5000}
                      prefix="₹"
                      colorClass="text-primary"
                    />
                    <p className="-mt-4 ml-1 text-[10px] font-bold uppercase tracking-wider text-amber-600">
                      * Only for Old Regime
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="mb-6 text-lg font-bold text-secondary">
                      Tax Comparison
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm">
                        <p className="mb-1 text-sm text-muted-foreground">
                          New Regime
                        </p>
                        <p
                          className={`text-2xl font-bold ${taxResult.new < taxResult.old ? "text-green-600" : "text-slate-800"}`}
                        >
                          {formatCurrency(taxResult.new)}
                        </p>
                        {taxResult.new < taxResult.old && (
                          <span className="text-[10px] font-bold uppercase text-green-500">
                            Recommended
                          </span>
                        )}
                      </div>
                      <div className="rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm">
                        <p className="mb-1 text-sm text-muted-foreground">
                          Old Regime
                        </p>
                        <p
                          className={`text-2xl font-bold ${taxResult.old < taxResult.new ? "text-green-600" : "text-slate-800"}`}
                        >
                          {formatCurrency(taxResult.old)}
                        </p>
                        {taxResult.old < taxResult.new && (
                          <span className="text-[10px] font-bold uppercase text-green-500">
                            Recommended
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 rounded-xl border border-primary/10 bg-primary/5 p-4">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-medium">
                          Total Savings
                        </span>
                        <span className="text-lg font-bold text-primary">
                          {formatCurrency(
                            Math.abs(taxResult.old - taxResult.new)
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Recommended regime:{" "}
                        <span className="font-bold text-secondary">
                          {taxResult.new < taxResult.old
                            ? "New Regime"
                            : "Old Regime"}
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
